import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { siteUrl } from "../components/Seo";
import ToolItem from "../components/ToolItem";
import { fetchStack, fetchVoted, upvoteTool } from "../lib/api";
import useBookmarks from "../lib/useBookmarks";
import { timeAgo } from "../lib/utils-tv";

/**
 * Public stack page — an immutable snapshot of a user's starred tools.
 * Indexable; each stack is its own SEO surface.
 */
export default function Stack() {
  const { id } = useParams();
  const [stack, setStack] = useState(null);
  const [status, setStatus] = useState("loading");
  const [voted, setVoted] = useState(new Set());
  const { ids: myStarred, replace } = useBookmarks();

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    fetchStack(id)
      .then((s) => {
        if (!alive) return;
        setStack(s);
        setStatus("ok");
      })
      .catch((err) => {
        if (!alive) return;
        setStatus(err?.response?.status === 404 ? "notfound" : "error");
      });
    fetchVoted()
      .then((vs) => alive && setVoted(new Set(vs)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id]);

  const onUpvote = async (toolId) => {
    if (voted.has(toolId)) return;
    try {
      const r = await upvoteTool(toolId);
      setVoted((p) => new Set(p).add(toolId));
      setStack((s) => ({
        ...s,
        tools: s.tools.map((t) =>
          t.id === toolId ? { ...t, upvotes: r.upvotes } : t,
        ),
      }));
    } catch {
      toast.error("upvote failed");
    }
  };

  // Fork = copy all tool ids into the visitor's localStorage stack.
  const onFork = () => {
    if (!stack?.tools?.length) return;
    const ids = stack.tools.map((t) => t.id);
    const merged = Array.from(new Set([...myStarred, ...ids]));
    replace(merged);
    toast.success(`forked ${ids.length} tools into your stack`);
  };

  if (status === "loading") {
    return (
      <div data-testid="stack-loading">
        <Seo title="Loading stack" path={`/stack/${id}`} noindex />
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-10">
          <div className="text-[13px] text-[#888]">
            loading stack...<span className="tv-blink">_</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div data-testid="stack-404">
        <Seo title="Stack not found" path={`/stack/${id}`} noindex />
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="text-xl font-bold border-b border-black pb-2">
            {">"}_ 404 / no such stack
          </h1>
          <p className="mt-4 text-[13px] text-[#444]">
            That stack ID doesn&apos;t exist. It may have been mistyped.
          </p>
          <p className="mt-4">
            <Link to="/" className="tv-btn">
              [← back to index]
            </Link>
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "error" || !stack) {
    return (
      <div data-testid="stack-error">
        <Seo title="Error" path={`/stack/${id}`} noindex />
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="text-xl font-bold">{">"}_ error</h1>
          <p className="text-[13px] text-[#444] mt-2">
            could not load stack. try again.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const title = stack.title || "a stack";
  const count = stack.tools.length;
  const sample = stack.tools
    .slice(0, 4)
    .map((t) => t.title)
    .join(", ");
  const seoDesc = `${count} hand-picked investing tools — ${sample}${
    count > 4 ? `, +${count - 4} more` : ""
  }. Curated via TerminalValue.io.`;

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Stack: ${title}`,
    numberOfItems: count,
    itemListElement: stack.tools.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: t.slug ? siteUrl(`/tool/${t.slug}`) : t.url,
      name: t.title,
    })),
  };

  return (
    <div data-testid="stack-page">
      <Seo
        title={`Stack: ${title}`}
        description={seoDesc}
        path={`/stack/${id}`}
        image={siteUrl(`/api/og/stack/${id}.png`)}
        ogType="article"
        noindex
        jsonLd={itemListLd}
      />
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="text-[12px] text-[#888] mb-3" data-testid="stack-breadcrumbs">
          <Link to="/" className="tv-link">
            index
          </Link>{" "}
          / stack / <span className="text-black">{id}</span>
        </div>

        <h1
          className="text-xl font-bold border-b border-black pb-2"
          data-testid="stack-title"
        >
          {">"}_ {title}
        </h1>

        <div className="mt-2 text-[12px] text-[#666] flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span data-testid="stack-meta">
            {count} {count === 1 ? "tool" : "tools"} · created{" "}
            {timeAgo(stack.created_at)}
          </span>
          <span className="text-[#bbb]">·</span>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("link copied");
              } catch {
                toast.error("could not copy");
              }
            }}
            className="tv-link"
            data-testid="stack-copy"
          >
            copy link
          </button>
          <span className="text-[#bbb]">·</span>
          <button
            type="button"
            onClick={onFork}
            className="tv-link"
            data-testid="stack-fork"
            title="Copy these tools into your own stack (stored locally)"
          >
            fork into my stack
          </button>
          <span className="text-[#bbb]">·</span>
          <a
            href={`https://x.com/intent/post?text=${encodeURIComponent(
              `${title} — ${count} investing tools`,
            )}&url=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tv-link"
            data-testid="stack-share-twitter"
          >
            x
          </a>
        </div>

        {/* Tool list — reuses ToolItem for consistency */}
        <ol className="list-none p-0 m-0 mt-4">
          {stack.tools.map((t, i) => (
            <ToolItem
              key={t.id}
              index={i + 1}
              tool={t}
              voted={voted.has(t.id)}
              onUpvote={onUpvote}
            />
          ))}
        </ol>

        <div className="mt-8 text-[12px] text-[#666] border-t border-[#eee] pt-4">
          <p>
            this is a snapshot stack created on{" "}
            {new Date(stack.created_at).toLocaleDateString()}. tools
            change; the snapshot doesn&apos;t.
          </p>
          <p className="mt-2">
            <Link to="/" className="tv-link">
              [← back to index]
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
